import Immutable from 'immutable';

import type { DashboardVideo, LanguageLocalesEnum } from 'js/src/client';
import { ApiService, RoleEnum, TaskResultMessageTypeEnum, VideoStatus } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { storeProjects } from 'js/src/data/account';
import {
    addViews,
    setActiveView,
    setLoadingDashboard,
    storeLoadedUpToPage,
    storeMoreVideosToLoad,
    storeVideos,
    updateVideoProperty,
} from 'js/src/data/dashboard/dashboard-actions';
import { startPollingEstimatedRenderDurations } from 'js/src/data/dashboard/dashboard-operations';
import { pathForView } from 'js/src/data/dashboard/dashboard-routes';
import {
    getVideoSearchTerm,
    getVideosSortBy,
    isViewEmpty,
    selectActiveView,
    selectFilterVideosByUserId,
    selectLoadedUpToPage,
    selectNumVideos,
    selectVideoById,
    selectVideoByIdInView,
    selectViewData,
} from 'js/src/data/dashboard/dashboard-selectors';
import { ALL_VIDEOS_VIEW_ID, TRASH_VIDEOS_VIEW_ID } from 'js/src/data/dashboard/dashboard-types';
import { convertProjectToView } from 'js/src/data/dashboard/dashboard-utils';
import { loadLuminaryPreviewsOperation } from 'js/src/data/thumbnail-previews';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import WebSocketClient from 'js/src/libs/websocket-client';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

const VIDEO_PAGE_SIZE = 20;
const MOBILE_VIDEO_PAGE_SIZE = 10;

/**
 * We have 2 separate redux states for videos in projects and videos in the whole workspace, but the operation
 * is the same should we need to regenerate results for the user when they bulk delete/move. This helper function
 * takes how many pages of results was shown to the user
 */
const _reloadPages = async ({
    // pagination args
    loadedUpToPage,
    pagesToReload = 0,
    // callback to run
    loadVideosCallback,
}: {
    loadedUpToPage: number;
    pagesToReload?: number;
    loadVideosCallback: (page: number, isReload: boolean) => Promise<void>;
}) => {
    for (let pageToReload = pagesToReload; pageToReload > 0; pageToReload--) {
        const offsetFromPage = pageToReload - 1;
        const currentPageToReload = loadedUpToPage - offsetFromPage;
        try {
            await loadVideosCallback(currentPageToReload, true);
        } catch (err) {
            // do nothing!
        }
    }
};

export const triggerLoadingLuminaryPreviewsOperation = createLuminaryOperation(
    async ({ call }, videoList: Immutable.List<ImmutableMap<DashboardVideo>>) => {
        // iterate through the video list and find ones we should generate previews for
        const videosToGeneratePreviewsFor = videoList
            .filter((video) => !video.get('placeholder_image_url'))
            .filter((video) => !video.get('is_in_script_composer'));
        const videoIds = videosToGeneratePreviewsFor.map((video) => video.get('id'));

        // one by one, for each video to generate previews for, fetch the video data and then call the operation
        for (const videoId of videoIds) {
            // fetch the video data
            const video_response = await ApiService.videoRetrieve({
                path: { video_id: videoId.toString() },
                query: { require_editable_version: false },
            });
            const videoData = video_response.data.video_data;
            call(loadLuminaryPreviewsOperation, {
                newVideoDataList: [videoData],
                newIdList: Immutable.List([videoId]),
            });

            // to avoid hitting rate limits wait 200ms between each video
            await new Promise((resolve) => setTimeout(resolve, 200));
        }
    },
);

/**
 * Ajax operation to duplicate videos in a workspace/project. Operation includes generating thumbnail previews on success
 *
 * @param {Array<Number>} videoIds - video ids to be copied
 * @param {Number|undefined} projectOrmId - used to update projects reducer on success
 * @param {func} beforeCallback - do any preflight operations, like setting loading spinners
 * @returns {Promise} - chainable Promise to perform actions on success/error
 */
export const duplicateVideosOperation = createLuminaryOperation(
    async (
        { dispatch, getState, call },
        videoIds: number[],
        beforeCallback?: () => void,
    ): Promise<number[]> => {
        return new Promise<number[]>((resolve, reject) => {
            const activeView = selectActiveView(getState());
            beforeCallback && beforeCallback();
            ApiService.videosDuplicateVideosCreate({
                body: {
                    video_ids: videoIds,
                    with_prefix: true,
                },
            })
                .then((response) => {
                    const videos = makeVideosImmutable(response.data);
                    dispatch(
                        storeVideos({
                            view: activeView,
                            videos,
                            addToEnd: false,
                        }),
                    );
                    call(triggerLoadingLuminaryPreviewsOperation, videos);
                    const duplicatedVideoIds = videos.map((video) => video.get('id')).toArray();
                    resolve(duplicatedVideoIds);
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    reject(error);
                });
        });
    },
);

/**
 * Translate a video entirely on the backend. Duplicates the video, starts
 * the translation task, and adds the video (PROCESSING) to the dashboard.
 * When the task completes via WebSocket, the video status is updated to DRAFT.
 */
export const translateVideoBackendOperation = createLuminaryOperation(
    async (
        { dispatch, getState, call },
        videoId: number,
        targetLanguage: LanguageLocalesEnum,
        sourceLanguage?: string,
    ): Promise<number> => {
        const activeView = selectActiveView(getState());

        const response = await ApiService.translateVideoBackendCreate({
            body: {
                video_id: videoId,
                target_language: targetLanguage,
                source_language: sourceLanguage ?? '',
            },
        });

        const { video: videoData, task_id: taskId } = response.data;
        const videos = makeVideosImmutable([videoData as DashboardVideo]);
        dispatch(
            storeVideos({
                view: activeView,
                videos,
                addToEnd: false,
            }),
        );

        const duplicatedVideoId = (videoData as DashboardVideo).id;

        // Listen for task completion via WebSocket and update the video status
        const ws = WebSocketClient.getInstance();
        ws.waitForMessage(taskId, TaskResultMessageTypeEnum.TASK_RESULT, 'translate_video_backend')
            .then(() => {
                dispatch(
                    updateVideoProperty({
                        videoId: duplicatedVideoId,
                        property: 'status',
                        newValue: VideoStatus.DRAFT,
                    }),
                );
                // Clear the stale source-language poster so the tile
                // falls through to a live Luminary rendering of the
                // translated video.
                dispatch(
                    updateVideoProperty({
                        videoId: duplicatedVideoId,
                        property: 'placeholder_image_url',
                        newValue: null,
                    }),
                );
                // Fetch the translated video_data and generate a Luminary
                // thumbnail so the tile shows the translated content immediately.
                const video = selectVideoById(getState(), duplicatedVideoId);
                if (video) {
                    call(triggerLoadingLuminaryPreviewsOperation, Immutable.List([video]));
                }
            })
            .catch(() => {
                dispatch(
                    updateVideoProperty({
                        videoId: duplicatedVideoId,
                        property: 'status',
                        newValue: VideoStatus.DRAFT,
                    }),
                );
                Toast.error('Translation failed. You can still edit the video.');
            });

        return duplicatedVideoId;
    },
);

export const loadVideosOperation = createLuminaryOperation(
    ({ dispatch, getState, call }, page = 1) => {
        const isMobile = window.innerWidth < 768;
        const videoPageSize = isMobile ? MOBILE_VIDEO_PAGE_SIZE : VIDEO_PAGE_SIZE;

        const state = getState();
        const activeView = selectActiveView(state);
        const searchTerm = getVideoSearchTerm(state);
        const loadedUpToPage = selectLoadedUpToPage(state);
        const numResultsCurrentlyShowing = selectNumVideos(state);
        const numResultsToShow = loadedUpToPage * videoPageSize;
        const pagesToReload = Math.ceil(
            (numResultsToShow - numResultsCurrentlyShowing) / videoPageSize,
        );
        const sortBy = getVideosSortBy(state);
        const filterByUserId = selectFilterVideosByUserId(state);

        // if we need to reload pages, the API calls will be inside a loop, so we create a wrapper function
        // that can handle reloading vs fetching new results
        const _loadVideosOperation = (page: number, isReload = false) => {
            // the activeView is the project ID unless it's a special view
            const projectId = [ALL_VIDEOS_VIEW_ID, TRASH_VIDEOS_VIEW_ID].includes(activeView)
                ? undefined
                : activeView;
            const onlyArchived = activeView === TRASH_VIDEOS_VIEW_ID;

            return new Promise<void>((resolve, reject) => {
                ApiService.videoList({
                    query: {
                        filter_by_user_id: filterByUserId,
                        only_archived: onlyArchived,
                        page,
                        page_size: videoPageSize,
                        project_id: projectId,
                        search: searchTerm,
                        sort_by: sortBy,
                    },
                })
                    .then((response) => {
                        if (response.data.results === undefined) {
                            dispatch(setLoadingDashboard(false));
                            resolve();
                            return;
                        }
                        const currentSearchTerm = getVideoSearchTerm(state);
                        if (currentSearchTerm !== searchTerm) {
                            // we were searching, but the search term has changed since the request
                            // was sent. Drop the results here!
                            resolve();
                            return;
                        }
                        // }
                        // when the call returns, there's a chance that the active view might have changed
                        // so we want to now specify which view we are looking in / saving to.
                        const conditionToDismissLoader = !isReload
                            ? page > loadedUpToPage
                            : page <= loadedUpToPage;
                        if (conditionToDismissLoader) {
                            // get any videos we don't already have
                            const newVideos: DashboardVideo[] = [];
                            response.data.results.forEach((video) => {
                                if (!selectVideoByIdInView(state, video.id, activeView)) {
                                    newVideos.push(video);
                                }
                            });

                            // store videos
                            const videos = makeVideosImmutable(newVideos);
                            dispatch(
                                storeMoreVideosToLoad({
                                    view: activeView,
                                    hasMoreVideos: !!response.data.next,
                                }),
                            );
                            !isReload &&
                                dispatch(
                                    storeLoadedUpToPage({
                                        view: activeView,
                                        loadedUpToPage: page,
                                    }),
                                );
                            dispatch(
                                storeVideos({
                                    view: activeView,
                                    addToEnd: true,
                                    videos,
                                }),
                            );
                            void call(triggerLoadingLuminaryPreviewsOperation, videos);
                            dispatch(startPollingEstimatedRenderDurations());
                        } else {
                            dispatch(setLoadingDashboard(false));
                        }

                        resolve();
                    })
                    .catch((error: unknown) => {
                        reject(error);
                    });
            });
        };

        pagesToReload > 0
            ? _reloadPages({
                  loadedUpToPage,
                  pagesToReload,
                  loadVideosCallback: _loadVideosOperation,
              })
            : _loadVideosOperation(page);
    },
);

export const switchToVideoViewOperation = createLuminaryOperation(
    (
        { dispatch, getState, call },
        view: number, // project ID or special view ID
    ) => {
        const state = getState();
        const viewData = selectViewData(state, view);
        if (!viewData) {
            ErrorReporter.capture(new Error('No view data found for project ID'));
            return;
        }
        if (isViewEmpty(state, view)) {
            dispatch(setLoadingDashboard(true));
            dispatch(setActiveView({ view: view }));
            call(loadVideosOperation);
        } else {
            dispatch(setActiveView({ view: view }));
        }
    },
);

export const loadAllProjectsOperation = createLuminaryOperation(({ dispatch, getState }) => {
    ApiService.projectsList()
        .then((response) => {
            dispatch(storeProjects({ projects: response.data }));
            const allProjects = ImmutableFromJS(response.data);
            const projectViews = allProjects.map((project) => convertProjectToView(project));
            dispatch(
                addViews({
                    views: projectViews,
                }),
            );

            // Editors without the home tab flag have no Create view, so the
            // All Videos default leaves them without a visible "create video"
            // button — drop them into their first project instead. Editors who
            // do have the flag stay on /create/ (the proper home), unless
            // they've already deep-linked to a specific view.
            if (SERVER.USER.role === RoleEnum.EDITOR && !Flags.canSeeHomeTab()) {
                const activeView = selectActiveView(getState());
                if (activeView === ALL_VIDEOS_VIEW_ID) {
                    const firstProject = allProjects.find(
                        (project) => project.get('workspace') === SERVER.USER.workspace_id,
                    );
                    if (firstProject) {
                        const projectId = firstProject.get('id');
                        window.history.replaceState(null, '', pathForView(projectId));
                        dispatch(setActiveView({ view: projectId }));
                    }
                }
            }
        })
        .catch((error: unknown) => {
            ErrorReporter.capture(error);
            Toast.error('Error loading projects. Please try again.');
        });
});

/**
 * This method takes a standard JS list of video objects from the server and returns them as an Immutable list
 */
function makeVideosImmutable(
    newVideos: DashboardVideo[],
): Immutable.List<ImmutableMap<DashboardVideo>> {
    let videos = Immutable.List<ImmutableMap<DashboardVideo>>();

    for (let i = 0; i < newVideos.length; i++) {
        try {
            const immutableVideo = ImmutableFromJS(newVideos[i]);
            videos = videos.push(immutableVideo);
        } catch (err: unknown) {
            ErrorReporter.capture(err);
        }
    }
    return videos;
}
